/* eslint-disable no-use-before-define, no-unused-vars */
import _ from 'underscore';
import { promisifyObservable } from './promisify-observable';
import {
  IObservable,
  IObserver,
  ISubscriberFunction,
  ISubscription,
  ISubscriptionObserver,
} from './types';

class SubscriptionObserver<T = unknown, E = unknown> implements ISubscriptionObserver<T, E> {
  subscription: Subscription<T, E>;

  complete: () => void;

  error: (error: E) => void;

  next: (value: T) => void;

  constructor({
    start = _.noop,
    next = _.noop,
    error = _.noop,
    complete = _.noop,
  }: IObserver<T, E>, subscription: Subscription<T, E>) {
    this.next = next;
    this.error = error;
    this.complete = complete;
    start(subscription);
    this.subscription = subscription;
  }

  get closed() {
    return this.subscription.closed;
  }
}

class Subscription<T = unknown, E = unknown> implements ISubscription<T, E> {
  observer: ISubscriptionObserver<T, E>;

  isClosed: boolean;

  unsubscribeCallback: () => void;

  constructor(observer: IObserver<T, E>, subscriber: ISubscriberFunction<T, E>) {
    this.observer = new SubscriptionObserver(observer, this);
    this.unsubscribeCallback = subscriber(this.observer) || _.noop;
    this.isClosed = false;
  }

  unsubscribe() {
    this.unsubscribeCallback();
    this.isClosed = true;
  }

  get closed() {
    return this.isClosed;
  }
}

/**
 * A minimum implementation of https://github.com/tc39/proposal-observable
 */
export class Observable<T = unknown, E = unknown> implements IObservable<T, E> {
  subscriber: ISubscriberFunction<T, E>;

  constructor(subscriber: ISubscriberFunction<T, E>) {
    this.subscriber = subscriber;
  }

  subscribe(observer: IObserver<T, E>): ISubscription<T, E> {
    return new Subscription(observer, this.subscriber);
  }

  toPromise(): ReturnType<IObservable<T, E>['toPromise']> {
    return promisifyObservable(this);
  }
}
